import { useState, useEffect } from 'react'
import { useRequests } from '../../../requests/useRequests'
import { Plant } from '../../../types/plant'
import { FilterState } from '../types/plant-filter.types'

export const usePlantSearch = (filters: FilterState, searchTerm: string) => {
  const requests = useRequests()

  // Stan dla roślin i wyników wyszukiwania
  const [filteredPlants, setFilteredPlants] = useState<Plant[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Stan dla paginacji
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalPlants, setTotalPlants] = useState(0)
  const [pageSize, setPageSize] = useState(12) // Ilość roślin na stronę

  // Stan dla zdebounce'owanej wartości wyszukiwania
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)

  // Efekt do obsługi debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500) // 500ms debounce

    return () => {
      clearTimeout(timer)
    }
  }, [searchTerm])

  // Funkcja do oznaczania obrazu jako ładujący się
  const initializeImageLoading = (plants: Plant[]) => {
    const newLoadingState: Record<number, boolean> = {}
    plants.forEach(plant => {
      newLoadingState[plant.id] = true
    })
  }

  // Pobieranie roślin z API z uwzględnieniem filtrów i paginacji
  useEffect(() => {
    const fetchPlants = async () => {
      setLoading(true)
      try {
        const params = {
          page: currentPage,
          limit: pageSize,
          ...(filters.category.length > 0 && { category: filters.category }),
          ...(filters.flowerColor.length > 0 && { flowerColor: filters.flowerColor }),
          ...(filters.soilType.length > 0 && { soilType: filters.soilType }),
          ...(filters.sunExposure.length > 0 && { sunExposure: filters.sunExposure }),
          ...(filters.form.length > 0 && { form: filters.form }),
          ...(filters.habit.length > 0 && { habit: filters.habit }),
          ...(filters.leafType.length > 0 && { leafType: filters.leafType[0] }),
          ...(filters.soilMoisture.length > 0 && { soilMoisture: filters.soilMoisture }),
          ...(filters.soilPh.length > 0 && { soilPh: filters.soilPh }),
          ...(filters.features.length > 0 && { features: filters.features }),
          ...(filters.gardenTypes.length > 0 && { gardenTypes: filters.gardenTypes }),
          ...(filters.usage.length > 0 && { usage: filters.usage }),
          ...(filters.growthRate.length > 0 && { growthRate: filters.growthRate }),
          ...(filters.leafColor.length > 0 && { leafColor: filters.leafColor }),
          ...(filters.floweringSeason.length > 0 && { floweringSeason: filters.floweringSeason }),
          ...(filters.fruits.length > 0 && { fruits: filters.fruits[0] }),
          ...(filters.fruitingSeason.length > 0 && { fruitingSeason: filters.fruitingSeason }),
          ...(filters.hardiness.length > 0 && { hardiness: filters.hardiness }),
          ...(filters.heightMin && { heightMin: Number(filters.heightMin) }),
          ...(filters.heightMax && { heightMax: Number(filters.heightMax) }),
          ...(filters.spreadMin && { spreadMin: Number(filters.spreadMin) }),
          ...(filters.spreadMax && { spreadMax: Number(filters.spreadMax) }),
          ...(debouncedSearchTerm && { searchTerm: debouncedSearchTerm })
        }

        const response = await requests.getPlants({
          ...params,
          page: Number(params.page),
          limit: Number(params.limit)
        })
        const plantsData: Plant[] = response.data.plants

        // Inicjalizacja stanu ładowania zdjęć
        initializeImageLoading(plantsData)

        // OPTYMALIZACJA: Wyświetl karty roślin przed załadowaniem zdjęć
        setFilteredPlants(plantsData)

        // OPTYMALIZACJA: Pobierz presigned URL w tle tylko dla tych obrazów, które tego potrzebują
        setTimeout(() => {
          // Tylko obrazy główne wymagające presigned URL
          const needsPresignedUrl = plantsData
            .filter((plant: Plant) => plant.image && !isValidUrl(plant.image))
            .map((plant: Plant) => plant.id)

          // Jeśli mamy jakieś identyfikatory potrzebujące presigned URL
          if (needsPresignedUrl.length > 0) {
            requests.getBulkPlantPresignedUrls(needsPresignedUrl)
              .then(response => {
                // Zakładając, że response.data to mapa {plantId: imagePresignedUrl}
                setFilteredPlants(prev =>
                  prev.map(p => ({
                    ...p,
                    imagePresignedUrl: response.data[p.id] || p.imagePresignedUrl || (isValidUrl(p.image) ? p.image : null)
                  }))
                )
              })
              .catch(error => {
                console.error('Błąd podczas pobierania presigned URL dla roślin:', error)
              })
          }
        }, 100)

        setTotalPages(response.data.pagination.totalPages)
        setTotalPlants(response.data.pagination.totalCount)
        setError(null)
      } catch (err) {
        console.error('Błąd podczas pobierania roślin:', err)
        setError('Nie udało się pobrać listy roślin.')
        setFilteredPlants([])
      } finally {
        setLoading(false)
      }
    }

    fetchPlants()
  }, [currentPage, pageSize, filters, requests, debouncedSearchTerm])

  // Obsługa zmiany strony
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo(0, 0) // Przewiń do góry przy zmianie strony
  }

  // Obsługa zmiany ilości roślin na stronę
  const handleItemsPerPageChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
    window.scrollTo(0, 0)
  }

  // Funkcja pomocnicza do walidacji URL
  function isValidUrl (urlString: string | null | undefined): boolean {
    if (!urlString) return false

    try {
      // Sprawdź, czy to już pełny URL
      if (urlString.startsWith('http://') || urlString.startsWith('https://')) {
        return true
      }

      // Sprawdź, czy można utworzyć poprawny obiekt URL
      new URL(urlString)
      return true
    } catch {
      // Jeśli wystąpi błąd, to nie jest poprawny URL
      return false
    }
  }

  return {
    filteredPlants,
    loading,
    error,
    currentPage,
    totalPages,
    totalPlants,
    pageSize,
    handlePageChange,
    handleItemsPerPageChange,
    isValidUrl
  }
}
